import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import styles from './SellerApplyPage.module.css';
import api from '../api/vendly';

const NIGERIAN_STATES = ['Abia','Adamawa','Akwa Ibom','Anambra','Bauchi','Bayelsa','Benue','Borno','Cross River','Delta','Ebonyi','Edo','Ekiti','Enugu','FCT','Gombe','Imo','Jigawa','Kaduna','Kano','Katsina','Kebbi','Kogi','Kwara','Lagos','Nasarawa','Niger','Ogun','Ondo','Osun','Oyo','Plateau','Rivers','Sokoto','Taraba','Yobe','Zamfara'];

const CATEGORIES = ['Electronics','Fashion','Home & Living','Beauty','Sports','Gaming','Computing','Supermarket','Foodstuff','Mixed (Multiple categories)'];

const BANKS = ['Access Bank','Citibank','EcoBank','Fidelity Bank','First Bank','First City Monument Bank (FCMB)','Guaranty Trust Bank (GTB)','Heritage Bank','Keystone Bank','Polaris Bank','Providus Bank','Stanbic IBTC Bank','Standard Chartered','Sterling Bank','Suntrust Bank','Union Bank','United Bank for Africa (UBA)','Unity Bank','Wema Bank','Zenith Bank','Kuda Bank','Opay','PalmPay','Moniepoint'];

export default function SellerApplyPage() {
  const { user } = useAuth();
  const navigate  = useNavigate();

  const [step, setStep]       = useState(1); // 1=business, 2=contact, 3=bank, 4=verify
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');

  const [form, setForm] = useState({
    businessName: '', businessType: 'individual',
    category: '', description: '', location: '', state: '', address: '',
    phone: '', whatsapp: '', businessEmail: user?.email || '',
    bankName: '', accountNumber: '', accountName: '',
    idType: 'nin', idNumber: '',
    agreeTerms: false,
  });

  const set = (e) => {
    const { name, value, type, checked } = e.target;
    setForm(f => ({ ...f, [name]: type === 'checkbox' ? checked : value }));
  };

  const next = () => { setError(''); setStep(s => s + 1); };
  const back = () => { setError(''); setStep(s => s - 1); };

  const validateStep = () => {
    if (step === 1) {
      if (!form.businessName) return 'Business name is required';
      if (!form.category)     return 'Please select a category';
      if (!form.state)        return 'Please select your state';
      if (!form.address)      return 'Business address is required';
    }
    if (step === 2) {
      if (!form.phone) return 'Phone number is required';
    }
    if (step === 3) {
      if (!form.bankName)      return 'Bank name is required';
      if (!form.accountNumber) return 'Account number is required';
      if (!form.accountName)   return 'Account name is required';
      if (form.accountNumber.length < 10) return 'Account number must be at least 10 digits';
    }
    if (step === 4) {
      if (!form.idNumber)  return 'ID number is required';
      if (!form.agreeTerms) return 'You must agree to the terms and conditions';
    }
    return null;
  };

  const handleNext = () => {
    const err = validateStep();
    if (err) { setError(err); return; }
    if (step < 4) { next(); return; }
    submit();
  };

  const submit = async () => {
    setLoading(true);
    setError('');
    try {
      await api.post('/sellers/apply', form);
      navigate('/seller/applied');
    } catch (err) {
      const msg = err.response?.data?.error || 'Submission failed. Please try again.';
      if (err.response?.status === 409) {
        navigate('/seller/dashboard');
        return;
      }
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const steps = ['Business info', 'Contact', 'Bank details', 'Verification'];

  return (
    <div className={styles.page}>
      <div className={`container ${styles.layout}`}>

        {/* Left */}
        <div className={styles.left}>
          <Link to="/become-seller" className={styles.backBtn}>← Back</Link>
          <h1 className={styles.title}>Create seller account</h1>
          <p className={styles.sub}>Applying as <strong>{user?.name}</strong> · {user?.email}</p>

          {/* Step indicators */}
          <div className={styles.stepIndicators}>
            {steps.map((s, i) => (
              <div key={i} className={`${styles.stepInd} ${i + 1 === step ? styles.stepIndActive : i + 1 < step ? styles.stepIndDone : ''}`}>
                <div className={styles.stepIndDot}>
                  {i + 1 < step ? (
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="20 6 9 17 4 12"/>
                    </svg>
                  ) : i + 1}
                </div>
                <span className={styles.stepIndLabel}>{s}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Right — form */}
        <div className={styles.card}>
          {error && <div className={styles.error}>{error}</div>}

          {/* Step 1 — Business info */}
          {step === 1 && (
            <div className={styles.fields}>
              <h2 className={styles.stepTitle}>Business information</h2>

              <div className={styles.field}>
                <label className={styles.label}>Business / Shop name *</label>
                <input className={styles.input} name="businessName" value={form.businessName} onChange={set} placeholder="e.g. Chioma's Fashion Store" />
              </div>

              <div className={styles.field}>
                <label className={styles.label}>Business type *</label>
                <div className={styles.radioGroup}>
                  {[['individual','Individual / Personal'],['registered_business','Registered Business']].map(([v, l]) => (
                    <label key={v} className={`${styles.radioBtn} ${form.businessType === v ? styles.radioBtnActive : ''}`}>
                      <input type="radio" name="businessType" value={v} checked={form.businessType === v} onChange={set} />
                      {l}
                    </label>
                  ))}
                </div>
              </div>

              <div className={styles.formRow}>
                <div className={styles.field}>
                  <label className={styles.label}>Main category *</label>
                  <select className={styles.input} name="category" value={form.category} onChange={set}>
                    <option value="">Select category</option>
                    {CATEGORIES.map(c => <option key={c} value={c.toLowerCase()}>{c}</option>)}
                  </select>
                </div>
                <div className={styles.field}>
                  <label className={styles.label}>State *</label>
                  <select className={styles.input} name="state" value={form.state} onChange={set}>
                    <option value="">Select state</option>
                    {NIGERIAN_STATES.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
              </div>

              <div className={styles.field}>
                <label className={styles.label}>City / Area</label>
                <input className={styles.input} name="location" value={form.location} onChange={set} placeholder="e.g. Ikeja, Lagos" />
              </div>

              <div className={styles.field}>
                <label className={styles.label}>Business address *</label>
                <input className={styles.input} name="address" value={form.address} onChange={set} placeholder="Full address including street name" />
              </div>

              <div className={styles.field}>
                <label className={styles.label}>Business description</label>
                <textarea className={`${styles.input} ${styles.textarea}`} name="description" value={form.description} onChange={set} placeholder="Tell us about what you sell..." rows={3} />
              </div>
            </div>
          )}

          {/* Step 2 — Contact */}
          {step === 2 && (
            <div className={styles.fields}>
              <h2 className={styles.stepTitle}>Contact information</h2>

              <div className={styles.formRow}>
                <div className={styles.field}>
                  <label className={styles.label}>Phone number *</label>
                  <input className={styles.input} name="phone" value={form.phone} onChange={set} placeholder="08012345678" />
                </div>
                <div className={styles.field}>
                  <label className={styles.label}>WhatsApp number</label>
                  <input className={styles.input} name="whatsapp" value={form.whatsapp} onChange={set} placeholder="Same as phone if same" />
                </div>
              </div>

              <div className={styles.field}>
                <label className={styles.label}>Business email</label>
                <input className={styles.input} type="email" name="businessEmail" value={form.businessEmail} onChange={set} placeholder="business@email.com" />
              </div>
            </div>
          )}

          {/* Step 3 — Bank */}
          {step === 3 && (
            <div className={styles.fields}>
              <h2 className={styles.stepTitle}>Bank details for payouts</h2>
              <p className={styles.stepSub}>Your earnings will be sent here after each confirmed sale</p>

              <div className={styles.field}>
                <label className={styles.label}>Bank name *</label>
                <select className={styles.input} name="bankName" value={form.bankName} onChange={set}>
                  <option value="">Select your bank</option>
                  {BANKS.map(b => <option key={b} value={b}>{b}</option>)}
                </select>
              </div>

              <div className={styles.formRow}>
                <div className={styles.field}>
                  <label className={styles.label}>Account number *</label>
                  <input className={styles.input} name="accountNumber" value={form.accountNumber} onChange={set} placeholder="0123456789" maxLength={10} />
                </div>
                <div className={styles.field}>
                  <label className={styles.label}>Account name *</label>
                  <input className={styles.input} name="accountName" value={form.accountName} onChange={set} placeholder="As it appears on your account" />
                </div>
              </div>

              <div className={styles.bankNote}>
                🔒 Your bank details are encrypted and only used for seller payouts
              </div>
            </div>
          )}

          {/* Step 4 — Verification */}
          {step === 4 && (
            <div className={styles.fields}>
              <h2 className={styles.stepTitle}>Identity verification</h2>
              <p className={styles.stepSub}>Required to protect buyers and maintain seller quality</p>

              <div className={styles.field}>
                <label className={styles.label}>ID type *</label>
                <div className={styles.radioGroup}>
                  {[['nin','NIN'],['bvn','BVN'],['drivers_license','Driver\'s License'],['passport','Int\'l Passport']].map(([v, l]) => (
                    <label key={v} className={`${styles.radioBtn} ${form.idType === v ? styles.radioBtnActive : ''}`}>
                      <input type="radio" name="idType" value={v} checked={form.idType === v} onChange={set} />
                      {l}
                    </label>
                  ))}
                </div>
              </div>

              <div className={styles.field}>
                <label className={styles.label}>{form.idType.toUpperCase()} Number *</label>
                <input className={styles.input} name="idNumber" value={form.idNumber} onChange={set} placeholder={`Enter your ${form.idType.toUpperCase()} number`} />
              </div>

              {/* Summary */}
              <div className={styles.summary}>
                <h4 className={styles.summaryTitle}>Application summary</h4>
                <div className={styles.summaryGrid}>
                  <div><span>Business</span><strong>{form.businessName}</strong></div>
                  <div><span>Category</span><strong>{form.category}</strong></div>
                  <div><span>State</span><strong>{form.state}</strong></div>
                  <div><span>Bank</span><strong>{form.bankName}</strong></div>
                  <div><span>Account</span><strong>{form.accountNumber}</strong></div>
                </div>
              </div>

              <label className={styles.termsCheck}>
                <input type="checkbox" name="agreeTerms" checked={form.agreeTerms} onChange={set} />
                <span>
                  I agree to Vendly's <a href="#" className={styles.termsLink}>Seller Terms & Conditions</a>, confirm that all information provided is accurate, and understand that false information may result in account suspension.
                </span>
              </label>
            </div>
          )}

          {/* Navigation */}
          <div className={styles.navBtns}>
            {step > 1 && (
              <button className={styles.backNavBtn} onClick={back} disabled={loading}>← Back</button>
            )}
            <button className={styles.nextBtn} onClick={handleNext} disabled={loading}>
              {loading ? <span className={styles.spinner} /> : step === 4 ? 'Submit application' : 'Continue →'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
